import React, {
    useEffect,
    useContext,
    useState,
    useCallback,
} from "react";
import axios from "axios";
import fileDownload from "js-file-download";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";

import SubHeader from "../Layout/SubHeader";
import InfoModal from "../Layout/InfoModal";

import AddInventory from "./AddInventory";
import LocationsTable from "./LocationsTable";
import InventoryUploadResult from "./InventoryUploadResult";
import StoreSelector from "../Layout/StoreSelector";

import { SiteContext } from "../../context/SiteContext";
import { UserContext } from "../../context/UserContext";

import useSortableData from "../../hooks/SortableData";

import { getInventoryStores } from "../../helpers/Stores";

const Inventory = () => {
    const { env, csrfToken } = useContext(SiteContext);
    const { user } = useContext(UserContext);

    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [lastLoadTime, setLastLoadTime] = useState(new Date());
    const [locationsSummary, setLocationsSummary] = useState({
        locationCount: 0,
        itemCount: 0,
    });

    const [selectedLocations, setSelectedLocations] = useState([]);

    const [locationsEmptying, setLocationsEmptying] = useState(false);
    const [locationsRemoving, setLocationsRemoving] = useState(false);
    const [locationsUploading, setLocationsUploading] = useState(false);
    const [processingDownload, setProcessingDownload] = useState(false);

    const [infoModalData, setInfoModalData] = useState(null);

    const [locationFilters, setLocationFilters] = useState(
        {
            dateRangeOptions: [
                { id: "any", name: "Todo" },
                { id: "today", name: "Hoy" },
                { id: "last3Days", name: "Últimos 3 días" },
                { id: "last7Days", name: "Últimos 7 días" },
                { id: "last30Days", name: "Últimos 30 días" },
                { id: "custom", name: "Día específico" },
                { id: "range", name: "Rango personalizado" },
            ],
            date: {
                range: "any",
                from: null,
                until: null,
            },
            locationSearch: "",
            skuSearch: "",
            store: "101",
        }
    );

    const { items, requestSort, sortConfig } = useSortableData(locations, {
        direction: "ascending",
        key: "name",
    });

    const handleLocationSelect = (locationId, checked) => {
        if (checked) {
            setSelectedLocations((oldSelectedLocations) => {
                if (oldSelectedLocations.includes(locationId)) {
                    return oldSelectedLocations;
                } else {
                    return [...oldSelectedLocations, locationId];
                }
            });
        } else {
            setSelectedLocations((oldSelectedLocations) =>
                oldSelectedLocations.filter((id) => id !== locationId)
            );
        }
    };

    const hideInfoModal = useCallback(
        () => {
            setInfoModalData(infoModalData => ({
                ...infoModalData,
                show: false,
            }));
        }, []
    );

    const showInfoModal = useCallback(
        (infoModalData) => {
            setInfoModalData({
                ...infoModalData,
                show: true
            });
        }, []
    );

   const fetchLocations = useCallback(() => {
        setLocationsLoading(true);

        let query = "?store=" + encodeURIComponent(locationFilters.store) + "&";
        query += "locationSearch=" + encodeURIComponent(locationFilters.locationSearch) + "&";
        query += "skuSearch=" + encodeURIComponent(locationFilters.skuSearch);
        //query += filterDateToQuery("date", locationFilters.date);

        axios
            .get(env.webApiBaseUrl + "/inventoryLocations" + query, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-Token": csrfToken,
                    Authorization: "Bearer " + user.token,
                },
                withCredentials: true,
                timeout: 60000,
            })
            .then((locationsResponse) => {
                setLocations(locationsResponse.data.locations);
                setLocationsSummary(locationsResponse.data.summary);

                setSelectedLocations([]);
            })
            .catch((error) => {
                console.error(error);
                setInfoModalData({
                    type: "error",
                    msg: "No pude recuperar las ubicaciones",
                    closeButton: true,
                });
            })
            .finally(() => {
                setLocationsLoading(false);
                setLastLoadTime(new Date());
            });
    }, [env, csrfToken, user, setLocationsLoading, setLocations, locationFilters]);

    const handleFileUpload = useCallback(
        (file) => {
            if (!file) {
                return;
            }

            if (!["text/plain", "text/csv"].includes(file.type)) {
                setInfoModalData({
                    type: "error",
                    msg: "Formato no soportado",
                });
                return;
            }

            setLocationsUploading(true);

            showInfoModal({
                type: "loading",
                msg: "Subiendo archivo para agregar inventario",
                closeButton: false,
            });

            const formData = new FormData();
            formData.append(
                "inventory",
                file,
                file.name
            );

            const url = env.webApiBaseUrl + "/inventoryLocations/upload/" + 
                    encodeURIComponent(locationFilters.store);
            axios
                .post(url, formData, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                    timeout: 300000,
                })
                .then((response) => {
                    const existsErrors = response.data && 
                        response.data.errored && 
                        response.data.errored.length > 0;

                    showInfoModal({
                        size: "lg",
                        type: existsErrors ? "alert" : "success",
                        msg: <InventoryUploadResult
                                uploadResponse={response.data}
                            />
                    });
                })
                .catch((error) => {
                    console.error(error);
                    setInfoModalData({
                        type: "error",
                        msg: "No pude subir el inventario",
                    });
                })
                .finally(() => {
                    setLocationsUploading(false);
                    fetchLocations();
                });
        }, [env, csrfToken, user, showInfoModal, fetchLocations, locationFilters.store]
    );

    const handleEmptySelectedLocations = useCallback(
        () => {
            setLocationsEmptying(true);

            const request = {
                selectedLocations: selectedLocations,
            };
            axios
                .post(env.webApiBaseUrl + "/inventoryLocations/empty", request, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                })
                .then(() => {
                })
                .catch((error) => {
                    console.error(error);
                    setInfoModalData({
                        type: "error",
                        msg: "No pude vaciar las ubicaciones",
                    });
                })
                .finally(() => {
                    setLocationsEmptying(false);
                    fetchLocations();
                });
        }, [env, csrfToken, user, selectedLocations, fetchLocations]
    );

    const handleRemoveSelectedLocations = useCallback(
        () => {
            setLocationsRemoving(true);

            const request = {
                selectedLocations: selectedLocations,
            };
            axios
                .post(env.webApiBaseUrl + "/inventoryLocations/remove", request, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                })
                .then(() => {
                })
                .catch((error) => {
                    console.error(error);
                    showInfoModal({
                        type: "error",
                        msg: "No pude eliminar las ubicaciones",
                    });
                })
                .finally(() => {
                    setLocationsRemoving(false);
                    fetchLocations();
                });
        }, [env, csrfToken, user, selectedLocations, fetchLocations, showInfoModal]
    );

    const handleMoveLocationItem = useCallback(
        (location, item, newLocationStore, newLocationName) => {
            showInfoModal({
                type: "loading",
                msg: "Moviendo el SKU " + item.sku + " de la ubicación " + location.name + 
                    " a la bodega " + newLocationStore + " ubicación " + newLocationName,
                closeButton: false,
            });

            let query = "?store=" + encodeURIComponent(newLocationStore) + "&";
            query += "locationSearch=" + encodeURIComponent(newLocationName) + "&";

            axios
                .get(env.webApiBaseUrl + "/inventoryLocations" + query, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                    timeout: 60000,
                })
                .then((locationsResponse) => {
                    const locationsFound = locationsResponse.data.locations;
                    const newLocation = locationsFound.find(l => l.name === newLocationName);
                    if (!newLocation) {
                        setInfoModalData({
                            type: "error",
                            msg: "La nueva ubicación no existe",
                            closeButton: true,
                        });
                    }
                    else if (newLocation._id === location._id) {
                        setInfoModalData({
                            type: "error",
                            msg: "La nueva ubicación es igual a la ubicación actual",
                            closeButton: true,
                        });
                    }
                    else {
                        const request = {
                            newLocationId: newLocation._id,
                        };

                        const url = env.webApiBaseUrl + "/inventoryLocations/" + location._id + "/" + item._id;
                        axios
                            .patch(url, request, {
                                headers: {
                                    "X-Requested-With": "XMLHttpRequest",
                                    "X-CSRF-Token": csrfToken,
                                    Authorization: "Bearer " + user.token,
                                },
                                withCredentials: true,
                            })
                            .then((ordersResponse) => {
                                hideInfoModal();
                            })
                            .catch((error) => {
                                console.error(error);
                                showInfoModal({
                                    type: "error",
                                    msg: "No pude mover el SKU",
                                });
                            })
                            .finally(() => {
                                fetchLocations();
                            });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setInfoModalData({
                        type: "error",
                        msg: "No pude encontrar la nueva ubicación",
                        closeButton: true,
                    });
                });
        }, [env, csrfToken, user, showInfoModal, hideInfoModal, fetchLocations]
    );

    const applyRemoveLocationItem = useCallback(
        (location, item) => {
            showInfoModal({
                type: "loading",
                msg: "Eliminando el SKU " + item.sku + " de la ubicación " + location.name,
                closeButton: false,
            });

            axios
                .delete(env.webApiBaseUrl + "/inventoryLocations/" + location._id + "/" + item._id, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                })
                .then((ordersResponse) => {
                    hideInfoModal();
                })
                .catch((error) => {
                    console.error(error);
                    showInfoModal({
                        type: "error",
                        msg: "No pude eliminar el SKU de la ubicación",
                    });
                })
                .finally(() => {
                    fetchLocations();
                });
        }, [env, csrfToken, user, showInfoModal, hideInfoModal, fetchLocations]
    );

    const handleRemoveLocationItem = useCallback(
        (location, item) => {
            showInfoModal({
                type: "prompt",
                msg: "Confirmar eliminación del SKU " + item.sku + " de la ubicación " + location.name,
                ok: () => applyRemoveLocationItem(location, item),
                cancel: hideInfoModal,
                okButton: "ELIMINAR",
            });
        }, [showInfoModal, hideInfoModal, applyRemoveLocationItem]
    );

    const handleDownload = useCallback(
        () => {
            setProcessingDownload(true);

            const url = env.webApiBaseUrl + "/inventoryLocations/download/" + 
                    encodeURIComponent(locationFilters.store);
            axios
                .get(url, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-Token": csrfToken,
                        Authorization: "Bearer " + user.token,
                    },
                    withCredentials: true,
                    responseType: 'blob',
                })
                .then((response) => {
                    const fileContent = response.data;

                    const fecha = new Date();
                    const fechaLocalISO = fecha.getFullYear() +
                        "-" + String(fecha.getMonth() + 1).padStart(2, "0") +
                        "-" + String(fecha.getDate()).padStart(2, "0") +
                        "T" + String(fecha.getHours()).padStart(2, "0") +
                        "_" + String(fecha.getMinutes()).padStart(2, "0") +
                        "_" + String(fecha.getSeconds()).padStart(2, "0");
                    const filename = `inventory-${locationFilters.store}-${fechaLocalISO}.csv`;

                    fileDownload(fileContent, filename, "text/csv", "\uFEFF");
                })
                .catch((error) => {
                    console.error(error);
                    setInfoModalData({
                        type: "error",
                        msg: "No pude descargar el inventario.",
                    });
                })
                .finally(() => {
                    setProcessingDownload(false);
                });
        },
        [csrfToken, env.webApiBaseUrl, locationFilters.store, user.token],
    );

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations, locationFilters]);

    return (
        <div>
            <SubHeader>
                <div
                    className="float-end ms-2 mb-2"
                    style={{ marginTop: "0.1rem" }}
                >
                    <StoreSelector
                        className="float-end ms-2"
                        store={locationFilters.store}
                        availableStores={getInventoryStores()}
                        setStore={(store) => {
                            setLocationFilters(oldFilters => ({
                                ...oldFilters,
                                store: store,
                            }))
                        }}
                    />

                    <Button
                        variant="secondary"
                        className="float-end"
                        disabled={processingDownload}
                        onClick={() => handleDownload()}
                    >
                        Descargar BD
                    </Button>
                </div>

                <h2>
                    GESTIÓN DE INVENTARIO
                    <span className="quantities">
                        {locationsSummary && locationsSummary.locationCount != null
                        ? <Badge className="ms-3" bg="dark">
                            {locationsSummary.locationCount} ubicaciones
                        </Badge>
                        : null}
                        {locationsSummary && locationsSummary.itemCount != null
                        ? <Badge className="ms-2" bg="info">
                            {locationsSummary.itemCount} ítems
                        </Badge>
                        : null}
                    </span>
                </h2>
                <div className="w-100 d-flex justify-content-between mb-2">
                    <div>
                        Última actualización: {lastLoadTime.toLocaleString("es-CL")}
                    </div>
                    {locationsSummary
                    ? <div className="">
                        Espacios totales: {locationsSummary.slotCount}, 
                        espacios disponibles: {locationsSummary.availableCount}
                    </div>
                    : null
                    }
                </div>
            </SubHeader>

            <Container fluid>
                {infoModalData ? (
                    <InfoModal
                        closeButton={
                            infoModalData.closeButton !== undefined
                                ? infoModalData.closeButton
                                : true
                        }
                        type={infoModalData.type}
                        size={infoModalData.size}
                        msg={infoModalData.msg}
                        ok={infoModalData.ok}
                        cancel={infoModalData.cancel}
                        show={infoModalData.show}
                        close={hideInfoModal}
                        retry={hideInfoModal}
                        okButton={infoModalData.okButton}
                        cancelButton={infoModalData.cancelButton}
                    />
                ) : null}

                <AddInventory
                    setInfoModalData={showInfoModal}
                    handleFileUpload={handleFileUpload}
                    locationsUploading={locationsUploading}
                />

                <br />

                <LocationsTable
                    locations={items}
                    fetchLocations={fetchLocations}
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    selectedLocations={selectedLocations}
                    handleLocationSelect={handleLocationSelect}
                    locationFilters={locationFilters}
                    setLocationFilters={setLocationFilters}
                    locationsLoading={locationsLoading}
                    locationsEmptying={locationsEmptying}
                    locationsRemoving={locationsRemoving}
                    handleEmptySelectedLocations={handleEmptySelectedLocations}
                    handleRemoveSelectedLocations={handleRemoveSelectedLocations}
                    handleMoveLocationItem={handleMoveLocationItem}
                    handleRemoveLocationItem={handleRemoveLocationItem}
                />
            </Container>
        </div>
    );
};

export default Inventory;
